/**
 * How long the browser may hold incoming sound before it plays it, so a resent packet can still arrive in
 * time. Listeners far away (India to the US, about 250 ms there and back) heard many short dropouts; a
 * bigger hold gives resent packets time, and costs that much delay. Milliseconds, 0 to 4000; null leaves the
 * browser's own choice. Off for now: it is an experiment, to be turned on only after it is measured.
 */
export const LISTENER_BUFFER_MS: number | null = null;

/** The receiver of one remote sound, as the kit's consumer holds it. */
interface Listening {
  kind?: string;
  rtpReceiver?: RTCRtpReceiver & { jitterBufferTarget?: number | null };
}

/** The part of the kit's client this reads: its call statistics hold every consumer made for a remote person. */
interface Reachable {
  __internals__?: { callStats?: { consumers?: Map<string, Listening> } };
}

const done = new WeakSet<RTCRtpReceiver>();

/**
 * Applies the hold to each remote sound's receiver, once. The kit gives the page no receiver of its own, so
 * they are read from its call statistics' list of consumers (checked in @cloudflare/realtimekit 2.0.3); a
 * kit without that list, or a browser without the setting, is left as it is.
 */
export function holdListeners(client: object | null, ms: number | null = LISTENER_BUFFER_MS): void {
  if (ms === null) return;
  const consumers = (client as Reachable | null)?.__internals__?.callStats?.consumers;
  if (!consumers) return;
  for (const consumer of consumers.values()) {
    const receiver = consumer.kind === "audio" ? consumer.rtpReceiver : undefined;
    if (!receiver || done.has(receiver) || !("jitterBufferTarget" in receiver)) continue;
    done.add(receiver);
    receiver.jitterBufferTarget = ms;
  }
}
